// parser.js - JavaScript implementation of parsing and evaluation logic
// Based on grammar.pest and the Rust parser structure.

// --- Unit Definitions and Conversion Logic ---
// This section replaces the functionality of your `convert_chart.rs`.
// Populated based on the units defined in grammar.pest and verified against provided tests.
// Conversion factors are relative to a chosen base unit for each type.
// Please VERIFY these factors against your `convert_chart.rs` as floating point precision
// or slightly different definitions might exist.

const jsUnitType = {
    // Angle: Base unit is Radian (rad)
    Angle: {
        radian: 1,
        rad: 1,
        turn: Math.PI * 2, // 1 turn = 2π radians
        tr: Math.PI * 2,
        pal: Math.PI * 2, // Assuming "pal" is a synonym for turn
        degree: Math.PI / 180, // 1 degree = π/180 radians
        deg: Math.PI / 180,
        gradian: Math.PI / 200, // 1 gradian = π/200 radians
        grad: Math.PI / 200,
        gon: Math.PI / 200,
    },
    // Mass: Base unit is Kilogram (kg)
    Mass: {
        kilogram: 1,
        kg: 1,
        gram: 0.001,
        g: 0.001,
        milligram: 0.000001,
        mg: 0.000001,
        microgram: 0.000000001,
        microgramme: 0.000000001,
        mcg: 0.000000001,
        metric_ton: 1000,
        ton: 1000, // Assuming "ton" means metric ton here
        pound: 0.45359237, // International avoirdupois pound
        lb: 0.45359237,
        ounce: 0.028349523125, // International avoirdupois ounce (1/16 pound)
        stone: 6.35029318, // British imperial stone (14 pounds)
        short_ton: 907.18474, // US short ton (2000 pounds)
        long_ton: 1016.0469088, // UK long ton (2240 pounds)
    },
    // Length: Base unit is Meter (m)
    Length: {
        meter: 1,
        metre: 1,
        m: 1,
        centimeter: 0.01,
        centimetre: 0.01,
        cm: 0.01,
        millimeter: 0.001,
        millimetre: 0.001,
        mm: 0.001,
        kilometer: 1000,
        kilometre: 1000,
        km: 1000,
        inch: 0.0254,
        in: 0.0254,
        foot: 0.3048,
        ft: 0.3048,
        yard: 0.9144,
        yd: 0.9144,
        mile: 1609.344,
        mi: 1609.344,
        nautical_mile: 1852, // International nautical mile
        mni: 1852,
        // Add plural forms from grammar:
        millimetres: 0.001, millimeters: 0.001,
        centimeters: 0.01, centimetres: 0.01,
        metres: 1, meters: 1,
        kilometers: 1000, kilometres: 1000,
        inches: 0.0254,
        foots: 0.3048, feet: 0.3048,
        yards: 0.9144,
        miles: 1609.344,
        nautical_miles: 1852,
    },
    // Temperature: Requires special handling (non-linear conversion)
    // Base unit concept is less straightforward. Conversions are handled directly in jsConvert.
    Temperature: {
        celsius: 'CELSIUS', // Use string markers to indicate non-linear type
        'c': 'CELSIUS',
        fahrenheit: 'FAHRENHEIT',
        'f': 'FAHRENHEIT',
        kelvin: 'KELVIN',
        'k': 'KELVIN',
    },
    // Time: Base unit is Second (sec)
    Time: {
        second: 1,
        secs: 1, sec: 1,
        nanosecond: 1e-9,
        nanosecs: 1e-9, nanosec: 1e-9, ns: 1e-9,
        microsecond: 1e-6,
        microsecs: 1e-6, microsec: 1e-6, 'µs': 1e-6,
        millisecond: 0.001,
        millisecs: 0.001, millisec: 0.001, ms: 0.001,
        minute: 60,
        min: 60,
        hour: 3600,
        hrs: 3600, hr: 3600,
        day: 86400, // 24 * 3600
        week: 604800, // 7 * 86400
        wk: 604800, wks: 604800,
        month: 2629743.8333, // Average Gregorian month (30.436875 days * 86400 sec/day) - Matches test result
        mo: 2629743.8333, mos: 2629743.8333,
        year: 31556926, // Gregorian year (365.24219 days * 86400 sec/day) - Matches test result
        yr: 31556926, yrs: 31556926,
        decade: 315569260, // 10 years
        century: 3155692600, // 100 years
        centry: 3155692600,
        millenium: 31556926000, // 1000 years
        millenia: 31556926000, milleniums: 31556926000,
        // Add plural forms:
        seconds: 1, minutes: 60, hours: 3600, days: 86400, weeks: 604800, months: 2629743.8333, years: 31556926, decades: 315569260, centuries: 3155692600, milleniums: 31556926000,
    },
    // Area: Base unit is Square Meter (m2)
    Area: {
        sqm: 1, m2: 1, metre2: 1, meter2: 1, metres2: 1, meters2: 1,
        sqkm: 1_000_000, km2: 1_000_000, kilometre2: 1_000_000, kilometer2: 1_000_000, kilometres2: 1_000_000, kilometers2: 1_000_000, // (1000 m)^2
        hectare: 10000, ha: 10000, // 100m * 100m
        sqin: 0.00064516, in2: 0.00064516, inch2: 0.00064516, inches2: 0.00064516, // (0.0254 m)^2 - Matches test
        sqft: 0.09290304, ft2: 0.09290304, foot2: 0.09290304, feet2: 0.09290304, // (0.3048 m)^2 - Matches test
        sqyd: 0.83612736, yd2: 0.83612736, yard2: 0.83612736, yards2: 0.83612736, // (0.9144 m)^2 - Matches test
        acre: 4046.8564224, ac: 4046.8564224, // 43560 sq ft - Matches test
        sqmi: 2589988.110336, mi2: 2589988.110336, mile2: 2589988.110336, miles2: 2589988.110336, // (1609.344 m)^2 - Matches test
    },
    // Speed: Base unit is Meter per Second (mps)
    Speed: {
        mps: 1,
        kph: 1000 / 3600, kmh: 1000 / 3600, // (1000 m) / (3600 s)
        fps: 0.3048, // (0.3048 m) / (1 s)
        mph: 1609.344 / 3600, // (1609.344 m) / (3600 s)
        knot: 1852 / 3600, kn: 1852 / 3600, kt: 1852 / 3600, knots: 1852 / 3600, // (1852 m) / (3600 s)
    },
    // DigitalInformation: Base unit is Bit (bit) - **Using binary prefixes (powers of 1024) based on test results**
    DigitalInformation: {
        bit: 1, bits: 1,
        byte: 8, bytes: 8, // 1 byte = 8 bits
        kilobit: 1024, kbit: 1024, // 1 Kibibit = 1024 bits (based on test "1024 kilobits to kb")
        kilobyte: 1024 * 8, kb: 1024 * 8, // 1 Kibibyte = 1024 Bytes = 8192 bits (based on test "1024 bits to kb")
        megabit: 1024 * 1024, mbit: 1024 * 1024, // 1 Megibit
        megabyte: 1024 * 1024 * 8, mb: 1024 * 1024 * 8, // 1 Megibyte
        gigabit: Math.pow(1024, 3), gbit: Math.pow(1024, 3), // 1 Gigibit
        gigabyte: Math.pow(1024, 3) * 8, gb: Math.pow(1024, 3) * 8, // 1 Gigibyte
        terabit: Math.pow(1024, 4), tbit: Math.pow(1024, 4), // 1 Tebibit
        terabyte: Math.pow(1024, 4) * 8, tb: Math.pow(1024, 4) * 8, // 1 Tebibyte
        petabit: Math.pow(1024, 5), pbit: Math.pow(1024, 5), // 1 Pebibit
        petabyte: Math.pow(1024, 5) * 8, pb: Math.pow(1024, 5) * 8, // 1 Pebibyte
        exabit: Math.pow(1024, 6), ebit: Math.pow(1024, 6), // 1 Exbibit
        exabyte: Math.pow(1024, 6) * 8, eb: Math.pow(1024, 6) * 8, // 1 Exbibyte
        zettabit: Math.pow(1024, 7), zbit: Math.pow(1024, 7), // 1 Zebibit
        zettabyte: Math.pow(1024, 7) * 8, zb: Math.pow(1024, 7) * 8, // 1 Zebibyte
        yottabit: Math.pow(1024, 8), ybit: Math.pow(1024, 8), // 1 Yobibit
        yottabyte: Math.pow(1024, 8) * 8, yb: Math.pow(1024, 8) * 8, // 1 Yobibyte
    },
};

/**
 * Performs unit conversion.
 * Handles linear conversions via base units and special non-linear conversions (like Temperature).
 * @param {number} value - The numeric value to convert.
 * @param {string} fromUnitPath - The unit to convert from (e.g., "Weight.kg").
 * @param {string} toUnitPath - The unit to convert to (e.g., "Weight.g").
 * @returns {number} The converted value, or NaN if units are invalid or incompatible.
 */
const jsConvert = (value, fromUnitPath, toUnitPath) => {
    const [fromType, fromName] = fromUnitPath.split('.');
    const [toType, toName] = toUnitPath.split('.');

    // Validate unit types and names
    const fromUnitInfo = jsUnitType[fromType] ? jsUnitType[fromType][fromName] : undefined;
    const toUnitInfo = jsUnitType[toType] ? jsUnitType[toType][toName] : undefined;

    if (fromType !== toType || fromUnitInfo === undefined || toUnitInfo === undefined) {
        console.error(`Conversion Error: Invalid or incompatible units specified: ${fromUnitPath} to ${toUnitPath}`);
        return NaN; // Indicate conversion failure
    }

    // --- Special Handling for Temperature ---
    // Temperature conversions are non-linear and cannot use a simple base unit multiplication.
    if (fromType === 'Temperature') {
        // Convert 'value' from `fromName` to Kelvin (as an intermediate base)
        let kelvinValue;
        switch (fromName) {
            case 'celsius': case 'c': kelvinValue = value + 273.15; break;
            case 'fahrenheit': case 'f': kelvinValue = (value - 32) * 5/9 + 273.15; break;
            case 'kelvin': case 'k': kelvinValue = value; break;
            default: return NaN; // Should not happen if units are validated
        }

        // Convert from Kelvin to `toName`
        switch (toName) {
            case 'celsius': case 'c': return kelvinValue - 273.15;
            case 'fahrenheit': case 'f': return (kelvinValue - 273.15) * 9/5 + 32;
            case 'kelvin': case 'k': return kelvinValue;
            default: return NaN; // Should not happen if units are validated
        }
    }

    // --- Linear Conversions (for all other unit types) ---
    // Use the conversion factors relative to the base unit.
    const baseValue = value * fromUnitInfo; // Convert to the base unit's value
    const convertedValue = baseValue / toUnitInfo; // Convert from the base unit to the target unit

    return convertedValue;
};

// Helper to find the unit type for a given unit name (case-insensitive match)
const findUnitType = (unitName) => {
    const lowerUnitName = unitName.toLowerCase();
    for (const type in jsUnitType) {
        // Check if the lowercase unit name exists in the type's units
        if (jsUnitType[type][lowerUnitName] !== undefined) {
            return type;
        }
    }
    return null; // Not a recognized unit name
};


// --- Lexer (Tokenizer) ---
// Breaks the input string into a sequence of tokens based on grammar.pest.

const TOKEN_TYPES = {
    NUMBER: 'NUMBER',
    OPERATOR: 'OPERATOR', // +, -, *, /, %, ^, >>, <<
    KEYWORD_OPERATOR: 'KEYWORD_OPERATOR', // percentOf, percentOn
    KEYWORD_CONVERSION: 'KEYWORD_CONVERSION', // to, as, in
    IDENTIFIER: 'IDENTIFIER', // Potential function names or variable names (though assignment not evaluated)
    CONSTANT: 'CONSTANT', // pi, e, tau, π, τ
    UNIT: 'UNIT', // e.g., 'kg', 'g', 'm', 'ft', 'celsius', 'm2', 'kph'
    PAREN_OPEN: 'PAREN_OPEN', // (
    PAREN_CLOSE: 'PAREN_CLOSE', // )
    EQUALS: 'EQUALS', // = (for assignment, though not evaluated per line)
    EOF: 'EOF', // End of input marker
    UNKNOWN: 'UNKNOWN', // For characters/sequences that don't match any rule
    WHITESPACE: 'WHITESPACE', // Explicitly tokenize whitespace to skip it later
};

/**
 * Converts the input string into a sequence of tokens using regex patterns.
 * Prioritizes matching longer tokens and specific patterns first.
 * Handles case-insensitivity for keywords and units.
 * @param {string} input - The raw input string from the textarea.
 * @returns {Array<Object>} An array of token objects (excluding whitespace).
 */
const tokenize = (input) => {
    const tokens = [];
    let cursor = 0; // Current position in the input string

    // Define token patterns as regex, ordered by priority (longer/more specific first)
    const tokenPatterns = [
        { type: TOKEN_TYPES.WHITESPACE, pattern: /^\s+/ }, // Match and skip whitespace first
        // Multi-word keyword operators (case-insensitive, whole word)
        { type: TOKEN_TYPES.KEYWORD_OPERATOR, pattern: /^(percent\s+of|percent\s+on)\b/i, value: (match) => match[0].toLowerCase() === 'percent of' ? 'percentOf' : 'percentOn' },
        // Multi-word operator synonyms
        { type: TOKEN_TYPES.OPERATOR, pattern: /^(multiply\s+by|divide\s+by)\b/i, value: (match) => match[0].toLowerCase().startsWith('multiply') ? '*' : '/' },
        // Multi-character operators
        { type: TOKEN_TYPES.OPERATOR, pattern: /^(>>|<<)/ },
        // Multi-character keywords for conversion (whole word)
        { type: TOKEN_TYPES.KEYWORD_CONVERSION, pattern: /^(as|in)\b/i },
         // Single-character keyword for conversion (whole word)
        { type: TOKEN_TYPES.KEYWORD_CONVERSION, pattern: /^(to)\b/i },
        // Single-character operators and equals
        { type: TOKEN_TYPES.OPERATOR, pattern: /^(\+|\-|\*|\/|%|\^)/ }, // Exclude '=' here, handle separately
        { type: TOKEN_TYPES.EQUALS, pattern: /^=/ }, // Handle '=' separately
        { type: TOKEN_TYPES.PAREN_OPEN, pattern: /^\(/ },
        { type: TOKEN_TYPES.PAREN_CLOSE, pattern: /^\)/ },
        // Constant words (case-insensitive, whole word)
        { type: TOKEN_TYPES.CONSTANT, pattern: /^(pi|e|tau)\b/i },
        // Constant symbols
        { type: TOKEN_TYPES.CONSTANT, pattern: /^[πτ]/, value: (match) => match[0] === 'π' ? 'pi' : 'tau' },
        // Specific symbols for units (handle µs)
         { type: TOKEN_TYPES.UNIT, pattern: /^µs/i, value: (match) => match[0].toLowerCase() }, // Handle µs specifically
        // Prefixed units - need to be careful about order (e.g., ^kbit before ^k)
        { type: TOKEN_TYPES.UNIT, pattern: /^\^(kbit|mbit|gbit|tbit|pbit|ebit|zbit|ybit)\b/i, value: (match) => match[1].toLowerCase() },
        { type: TOKEN_TYPES.UNIT, pattern: /^\^(kb|mb|gb|tb|pb|eb|zb|yb)\b/i, value: (match) => match[1].toLowerCase() },
        { type: TOKEN_TYPES.UNIT, pattern: /^\^(c|f|k)\b/i, value: (match) => match[1].toLowerCase() },
        // Numbers (handle scientific notation)
        { type: TOKEN_TYPES.NUMBER, pattern: /^\d+(\.\d*)?([eE][+-]?\d+)?/ }, // Integer or decimal, optional scientific notation
        // Units and Identifiers (alphanumeric + underscore + apostrophe)
        // Match a potential identifier/unit string first, then check if it's a known unit.
        // This pattern needs to be after specific symbols and prefixed units.
        { type: 'IDENTIFIER_OR_UNIT', pattern: /^[a-zA-Z_][a-zA-Z0-9_']*/ },
        // Fallback for any single character not matched
        { type: TOKEN_TYPES.UNKNOWN, pattern: /^./ },
    ];

    while (cursor < input.length) {
        let matched = false;
        for (const tokenDef of tokenPatterns) {
            const match = input.substring(cursor).match(tokenDef.pattern);
            if (match) {
                // If it's whitespace, just skip and continue
                if (tokenDef.type === TOKEN_TYPES.WHITESPACE) {
                    cursor += match[0].length;
                    matched = true;
                    break; // Go to the next character in the input
                }

                // Handle the special case of 'IDENTIFIER_OR_UNIT'
                if (tokenDef.type === 'IDENTIFIER_OR_UNIT') {
                    const name = match[0];
                    const lowerName = name.toLowerCase();
                    const unitType = findUnitType(lowerName); // Check if it's a known unit
                    if (unitType) {
                        tokens.push({ type: TOKEN_TYPES.UNIT, value: lowerName, unitType: unitType });
                    } else {
                        tokens.push({ type: TOKEN_TYPES.IDENTIFIER, value: name }); // Keep original case for identifier
                    }
                    cursor += match[0].length;
                    matched = true;
                    break; // Go to the next character in the input
                }

                // For other token types, push the token and advance the cursor
                const tokenValue = tokenDef.value ? tokenDef.value(match) : match[0];
                tokens.push({ type: tokenDef.type, value: tokenValue });
                cursor += match[0].length;
                matched = true;
                break; // Go to the next character in the input
            }
        }

        if (!matched) {
            // This should ideally not happen if the patterns cover all possible inputs
            console.error(`Lexer Error: Could not tokenize input at position ${cursor}: "${input.substring(cursor, cursor + 10)}..."`);
            tokens.push({ type: TOKEN_TYPES.UNKNOWN, value: input[cursor] });
            cursor++;
        }
    }
    // Add EOF token
    tokens.push({ type: TOKEN_TYPES.EOF, value: '' });
    return tokens;
};

// --- Abstract Syntax Tree (AST) Node Types ---
// Represents the structure of the parsed expression.

const AST_TYPES = {
    NUMBER: 'NUMBER',
    BINARY_EXPRESSION: 'BINARY_EXPRESSION', // e.g., 2 + 3
    UNARY_MINUS: 'UNARY_MINUS', // e.g., -5
    FUNCTION_CALL: 'FUNCTION_CALL', // e.g., sin(90)
    CONSTANT: 'CONSTANT', // e.g., pi
    UNIT_CONVERSION: 'UNIT_CONVERSION', // e.g., 12 kg to g
    ASSIGNMENT: 'ASSIGNMENT', // e.g., x = 5 + 2 (Note: Evaluation only supports expressions per line currently)
    IDENTIFIER: 'IDENTIFIER', // For lone identifiers (variables)
};

// --- Operator Precedence and Associativity ---
// Defines the order in which operators are evaluated, mirroring the Rust PrecClimber.
// Higher number means higher precedence.

const PRECEDENCE = {
    '+': 1, '-': 1,
    '*': 2, '/': 2,
    '%': 3,
    '^': 4, // Power is Right Associative
    'percentOf': 5, 'percentOn': 5,
    '>>': 6, '<<': 6, // Bit Shifts are Right Associative based on Rust code
};

const ASSOCIATIVITY = {
    '+': 'Left', '-': 'Left',
    '*': 'Left', '/': 'Left',
    '%': 'Left',
    '^': 'Right',
    'percentOf': 'Left', 'percentOn': 'Left',
    '>>': 'Right', '<<': 'Right',
};


// --- Parser ---
// Builds an AST from the token sequence, respecting operator precedence.
// Implemented using recursive descent.

/**
 * Parses a sequence of tokens into an Abstract Syntax Tree (AST).
 * @param {Array<Object>} tokens - The array of tokens from the lexer.
 * @returns {Object} The root node of the AST.
 * @throws {Error} If a parsing error occurs (e.g., unexpected token).
 */
const parse = (tokens) => {
    let currentTokenIndex = 0; // State for the parser: current token position

    // Helper to look at the token at a specific offset from the current position
    const peek = (offset = 0) => {
        if (currentTokenIndex + offset >= tokens.length) {
             // Return a dummy EOF token if trying to peek beyond the end
             return { type: TOKEN_TYPES.EOF, value: '' };
        }
        return tokens[currentTokenIndex + offset];
    };

    // Helper to consume the current token, advancing the cursor, and optionally checking its type/value
    const consume = (type, value) => {
        const token = peek();
        if (token.type !== type || (value !== undefined && token.value !== value)) {
             // Handle parsing error: the next token wasn't what was expected
             throw new Error(`Parse Error: Expected token type ${type}` + (value !== undefined ? ` with value "${value}"` : '') + `, but found type ${token.type} with value "${token.value}" at position ${currentTokenIndex}.`);
        }
        currentTokenIndex++; // Move to the next token
        return token; // Return the consumed token
    };

    // Parses the highest precedence expressions: numbers, constants, parentheses,
    // function calls, and the special unit conversion pattern. Corresponds to the 'atom' rule.
    const parseAtom = () => {
        const token = peek();

        // --- Handle Unit Conversion ---
        // Grammar rule: `convert = { num ~ siunit ~ ( "to" | "as" | "in" ) ~ siunit }`
        // This pattern starts with a `num`. We need to check if the current token is a NUMBER
        // and if the subsequent tokens match the conversion pattern.
        // This check needs to happen BEFORE the general NUMBER handling, as it's a specific pattern.
        // Let's check the pattern using peek first.
        const maybeNum = peek();
        const maybeFromUnit = peek(1);
        const maybeToKeyword = peek(2);
        const maybeToUnit = peek(3);

        if (maybeNum.type === TOKEN_TYPES.NUMBER &&
            maybeFromUnit.type === TOKEN_TYPES.UNIT &&
            maybeToKeyword.type === TOKEN_TYPES.KEYWORD_CONVERSION &&
            maybeToUnit.type === TOKEN_TYPES.UNIT)
        {
            // Found the unit conversion pattern: NUMBER UNIT KEYWORD_CONVERSION UNIT
            // Consume the tokens belonging to this pattern
            const numberToken = consume(TOKEN_TYPES.NUMBER);
            const valueNode = { type: AST_TYPES.NUMBER, value: parseFloat(numberToken.value) }; // Create the number node

            const fromUnitToken = consume(TOKEN_TYPES.UNIT);
            consume(TOKEN_TYPES.KEYWORD_CONVERSION);
            const toUnitToken = consume(TOKEN_TYPES.UNIT);

            // Create a UNIT_CONVERSION AST node
            if (!fromUnitToken.unitType || !toUnitToken.unitType) {
                 throw new Error(`Parse Error: Unit token missing 'unitType'. Ensure lexer identifies unit types.`);
            }
            return {
                type: AST_TYPES.UNIT_CONVERSION,
                value: valueNode, // The AST node for the number
                fromUnit: { type: fromUnitToken.unitType, name: fromUnitToken.value },
                toUnit: { type: toUnitToken.unitType, name: toUnitToken.value },
            };
        }


        // --- Handle Numbers ---
        // This comes after the unit conversion check, so a number followed by a unit
        // will be correctly parsed as a conversion if it matches the pattern.
        if (token.type === TOKEN_TYPES.NUMBER) {
            return { type: AST_TYPES.NUMBER, value: parseFloat(consume(TOKEN_TYPES.NUMBER).value) };
        }

        // --- Handle Constants ---
        if (token.type === TOKEN_TYPES.CONSTANT) {
             const constantToken = consume(TOKEN_TYPES.CONSTANT);
             // Values are already mapped in lexer, but confirm here if needed
             const value = { 'pi': Math.PI, 'e': Math.E, 'tau': Math.PI * 2 }[constantToken.value];
             return { type: AST_TYPES.CONSTANT, name: constantToken.value, value };
        }

        // --- Handle Parenthesized Expressions ---
        if (token.type === TOKEN_TYPES.PAREN_OPEN) {
            consume(TOKEN_TYPES.PAREN_OPEN); // Consume the opening parenthesis
            const expression = parseExpression(); // Recursively parse the expression inside
            consume(TOKEN_TYPES.PAREN_CLOSE); // Expect and consume the closing parenthesis
            return expression; // The result of the inner expression is the value of this node
        }

        // --- Handle Function Calls or Identifiers ---
        // An IDENTIFIER can be a function name if followed by '('.
        // Otherwise, according to the grammar's 'atom' rule, a lone 'ident' is an atom.
        // However, the current evaluation logic only handles expressions, not variable lookups.
        // We'll parse it as an identifier node but note that evaluation won't support it.
        if (token.type === TOKEN_TYPES.IDENTIFIER) {
             const identifierToken = consume(TOKEN_TYPES.IDENTIFIER);
             // Check if the next token is an opening parenthesis (Function Call)
             if (peek().type === TOKEN_TYPES.PAREN_OPEN) {
                 consume(TOKEN_TYPES.PAREN_OPEN); // Consume the opening parenthesis
                 const argument = parseExpression(); // Parse the function argument (can be any expression)
                 consume(TOKEN_TYPES.PAREN_CLOSE); // Expect and consume the closing parenthesis
                 // Create a FUNCTION_CALL AST node
                 return { type: AST_TYPES.FUNCTION_CALL, name: identifierToken.value, argument };
             } else {
                 // It's just a lone identifier. The current evaluation doesn't support variables.
                 // We'll create an identifier node, but evaluation will likely return NaN.
                 console.warn(`Parser: Encountered identifier "${identifierToken.value}". Variable lookup is not supported in evaluation.`);
                 return { type: AST_TYPES.IDENTIFIER, name: identifierToken.value };
             }
        }

         // --- Handle Unary Minus ---
        // Check if the current token is '-' and the next is an atom.
        if (token.type === TOKEN_TYPES.OPERATOR && token.value === '-') {
            consume(TOKEN_TYPES.OPERATOR, '-'); // Consume the minus operator
            const argument = parseAtom(); // The operand of the unary minus is an atom
            // Create a UNARY_MINUS AST node
            return { type: AST_TYPES.UNARY_MINUS, argument };
        }


        // If none of the primary expression rules matched, it's a parsing error
        throw new Error(`Parse Error: Unexpected token type ${token.type} with value "${token.value}" at position ${currentTokenIndex}.`);
    };

    // Parses binary operations at a specific precedence level.
    // This function is called recursively for different precedence levels.
    // Corresponds to the `atom ~ (operation ~ atom)*` pattern in the grammar.
    const parseBinary = (precedenceLevel, parseLowerPrecedence) => {
        // Parse the left operand using the parsing function for the next lower precedence level
        let left = parseLowerPrecedence();

        // Loop as long as the current token is a binary operator at the current precedence level
        while (true) {
            const operatorToken = peek();
            // Check if the token is a standard OPERATOR or a KEYWORD_OPERATOR
            const isOperator = operatorToken.type === TOKEN_TYPES.OPERATOR || operatorToken.type === TOKEN_TYPES.KEYWORD_OPERATOR;

            // Check if the token is an operator at the current precedence level
            if (!isOperator || PRECEDENCE[operatorToken.value] !== precedenceLevel) {
                // If not, stop processing operators at this level and return the left operand
                break;
            }

            const operator = operatorToken.value;
            const associativity = ASSOCIATIVITY[operator];
            // Consume the operator token (either OPERATOR or KEYWORD_OPERATOR)
            consume(operatorToken.type, operator);

            // Parse the right operand. The parsing function used depends on associativity.
            const right = (associativity === 'Left')
                ? parseLowerPrecedence() // For left-associativity, the right operand is parsed at the next lower precedence
                : parseBinary(precedenceLevel, parseLowerPrecedence); // For right-associativity, the right operand is parsed at the *same* precedence level

            // Create a Binary Expression AST node with the left operand, operator, and right operand
            left = {
                type: AST_TYPES.BINARY_EXPRESSION,
                operator,
                left,
                right,
            };
        }

        return left; // Return the resulting AST node (could be a single atom or a binary expression tree)
    };

    // Define the parsing functions for each precedence level, chained together.
    // This structure mirrors the `PrecClimber` definition in the Rust code.
    // The lowest precedence level is the entry point for parsing a full expression.

    // Level 1: Add/Subtract (Left Associative)
    const parseAddSub = () => parseBinary(1, parseMulDivMod);
    // Level 2: Multiply/Divide (Left Associative)
    const parseMulDivMod = () => parseBinary(2, parseModulo);
    // Level 3: Modulo (Left Associative)
    const parseModulo = () => parseBinary(3, parsePower);
    // Level 4: Power (Right Associative)
    const parsePower = () => parseBinary(4, parsePercent);
    // Level 5: Percent Of/On (Left Associative)
    const parsePercent = () => parseBinary(5, parseShift);
     // Level 6: Bit Shifts (Right Associative based on Rust code)
    const parseShift = () => parseBinary(6, parseAtom); // The highest level delegates to parseAtom (which includes primary expressions)

    // The main entry point for parsing an expression (corresponds to the 'expr' rule)
    const parseExpression = parseAddSub; // Start the parsing process from the lowest precedence level


    // The top-level rule is `calculation = _{ SOI ~ stmt ~ EOI }`.
    // `stmt` can be `assign | expr`.
    // The current React component evaluates each line as an expression.
    // We will parse a statement, but if it's an assignment, the evaluator won't process it for a numeric result.
    const parseStatement = () => {
        const maybeIdentifier = peek();
        // Check if it looks like an assignment: IDENTIFIER followed by EQUALS
        if (maybeIdentifier.type === TOKEN_TYPES.IDENTIFIER && peek(1).type === TOKEN_TYPES.EQUALS) {
            const identifierToken = consume(TOKEN_TYPES.IDENTIFIER); // Consume identifier
            consume(TOKEN_TYPES.EQUALS); // Consume '='
            const expression = parseExpression(); // Parse the expression on the right side
            // Return an ASSIGNMENT AST node
            console.warn(`Parser: Assignment statement "${identifierToken.value} = ..." parsed, but variable assignment is not evaluated for a numeric result per line.`);
            return { type: AST_TYPES.ASSIGNMENT, name: identifierToken.value, value: expression };
        } else {
            // If it's not an assignment, parse it as a regular expression
            return parseExpression();
        }
    };


    // Start parsing the top-level statement and ensure the entire input (up to EOF) is consumed.
    const ast = parseStatement();
    consume(TOKEN_TYPES.EOF); // Expect and consume the EOF token after parsing the statement

    return ast; // Return the root of the constructed AST
};

// --- Evaluator (Interpreter) ---
// Traverses the AST and computes the final numeric result.

/**
 * Evaluates the given AST node to compute its numeric value.
 * Handles different AST node types (numbers, operations, functions, conversions).
 * @param {Object} node - The current AST node to evaluate.
 * @returns {number} The computed numeric value, or NaN if evaluation fails (e.g., division by zero, unknown function, unsupported node type).
 */
const evaluate = (node) => {
    switch (node.type) {
        case AST_TYPES.NUMBER:
            return node.value; // Return the numeric value of a number node
        case AST_TYPES.CONSTANT:
            return node.value; // Return the predefined value of a constant node
        case AST_TYPES.IDENTIFIER:
            // Evaluation of identifiers (variables) is not supported in this line-by-line calculator context.
            console.error(`Evaluation Error: Variable lookup for identifier "${node.name}" is not supported.`);
            return NaN;
        case AST_TYPES.ASSIGNMENT:
            // Assignment statements are parsed but not evaluated for a numeric result per line.
            // The value of the assignment itself is undefined in this context.
            console.warn(`Evaluation: Assignment statement parsed, but not evaluated for a numeric result.`);
            return NaN; // Assignments don't produce a numeric result for the line total
        case AST_TYPES.UNARY_MINUS:
            // Evaluate the argument and apply negation
            const unaryArg = evaluate(node.argument);
             if (isNaN(unaryArg)) return NaN;
            return -unaryArg;
        case AST_TYPES.BINARY_EXPRESSION:
            // Evaluate both left and right operands recursively
            const left = evaluate(node.left);
            const right = evaluate(node.right);

            // If either operand evaluated to NaN, the result is NaN
            if (isNaN(left) || isNaN(right)) {
                 return NaN;
            }

            // Perform the operation based on the operator
            switch (node.operator) {
                case '+': return left + right;
                case '-': return left - right;
                case '*': return left * right;
                case '/':
                     if (right === 0) {
                          // Handle division by zero explicitly
                          console.error("Evaluation Error: Division by zero.");
                          return NaN;
                     }
                     return left / right;
                case '%': return left % right;
                case '^': return Math.pow(left, right); // JavaScript's Math.pow for power
                case '>>': return Math.floor(left) >> Math.floor(right); // Bitwise right shift (operands are floored)
                case '<<': return Math.floor(left) << Math.floor(right); // Bitwise left shift (operands are floored)
                // Implement percentOf and percentOn logic matching the Rust functions
                case 'percentOf': return (left / 100) * right; // Corresponds to Rust's percent_of(a, b)
                case 'percentOn': return (left / 100) * right + right; // Corresponds to Rust's percent_on(a, b)
                default:
                    // Should not happen if the parser is correct, but handle unknown operators
                    console.error(`Evaluation Error: Unknown binary operator "${node.operator}"`);
                    return NaN;
            }
        case AST_TYPES.FUNCTION_CALL:
            // Evaluate the function argument
            const argument = evaluate(node.argument);

             // If the argument is NaN, the function result is NaN
            if (isNaN(argument)) {
                return NaN;
            }

            // Apply the function based on its name, mirroring Rust's apply_fun
            // Ensure function names are matched case-insensitively if needed, or match Rust's behavior.
            const functionName = node.name.toLowerCase();
            switch (functionName) {
                // Trigonometric functions: Rust's `to_radians()` suggests input is in degrees.
                // JavaScript's Math functions use radians, so convert degrees to radians.
                case 'sin': return Math.sin(argument * Math.PI / 180);
                case 'cos': return Math.cos(argument * Math.PI / 180);
                case 'tan': return Math.tan(argument * Math.PI / 180);
                // Inverse trigonometric functions: JavaScript's Math functions return radians.
                case 'asin': return Math.asin(argument);
                case 'acos': return Math.acos(argument);
                case 'atan': return Math.atan(argument);
                // Hyperbolic functions
                 case 'sinh': return Math.sinh(argument);
                 case 'cosh': return Math.cosh(argument);
                 case 'tanh': return Math.tanh(argument);
                 case 'asinh': return Math.asinh(argument);
                 case 'acosh': return Math.acosh(argument);
                 case 'atanh': return Math.atanh(argument);
                // Logarithms and roots
                case 'log': return Math.log10(argument); // Rust's log() is log base 10
                case 'sqrt': return Math.sqrt(argument);
                case 'cbrt': return Math.cbrt(argument);
                // Rounding functions
                case 'round': return Math.round(argument);
                case 'ceil': return Math.ceil(argument);
                case 'floor': return Math.floor(argument);
                default:
                    // Unknown function name
                    console.error(`Evaluation Error: Unknown function "${node.name}"`);
                    return NaN;
            }
         case AST_TYPES.UNIT_CONVERSION:
              // Evaluate the numeric value part of the conversion
             const valueToConvert = evaluate(node.value);

             // If the value to convert is NaN, the result is NaN
             if (isNaN(valueToConvert)) {
                 return NaN;
             }

             // Perform the unit conversion using the jsConvert function
             // Pass unit paths like "Weight.kg"
              const result = jsConvert(valueToConvert, `${node.fromUnit.type}.${node.fromUnit.name}`, `${node.toUnit.type}.${node.toUnit.name}`);
              return result;
        default:
            // Should not happen if the AST is correctly constructed, but handle unknown node types
            console.error(`Evaluation Error: Unknown AST node type "${node.type}"`);
            return NaN;
    }
};


// --- Formatting Function ---
// Corresponds to the `transform` function in the Rust code.

/**
 * Formats a number for display, similar to Rust's float_pretty_print.
 * @param {number} c - The number to format.
 * @returns {string} The formatted string.
 */
export const jsTransform = (c) => {
    if (isNaN(c)) {
        return "-"; // Display "-" for NaN
    }
    if (!isFinite(c)) {
        return c.toString(); // Display Infinity, -Infinity
    }
    if (c % 1 === 0) {
        return c.toString(); // Display as integer if it has no fractional part
    }
    // Format with a fixed number of decimal places and remove trailing zeros
    // Adjust the number of decimal places (e.g., 15-17 for f64 precision) as needed for desired precision.
    // Using toPrecision might also be an option depending on desired output style.
    // Let's use toPrecision(15) to get closer to f64 precision in Rust.
    return parseFloat(c.toPrecision(15)).toString();
};


// --- Main Parse Function ---
// The entry point for parsing and evaluating a single line of input.
// Corresponds to the `pub fn parse` function in the Rust code.

/**
 * Parses and evaluates a single line of input.
 * @param {string} input - The input string (a single line from the textarea).
 * @returns {number} The computed result, or NaN if parsing or evaluation fails.
 */
export const jsParse = (input) => {
    // Handle empty or whitespace-only input lines
    if (!input || input.trim() === "") {
        return NaN;
    }

    try {
        // 1. Tokenize the input string
        const tokens = tokenize(input);

        // Basic check for unknown tokens immediately after tokenizing
        const unknownToken = tokens.find(token => token.type === TOKEN_TYPES.UNKNOWN);
        if (unknownToken) {
             console.error(`Input Error: Contains unrecognized character(s).`);
             // Return NaN or throw a more specific error
             return NaN;
        }

        // 2. Parse the tokens into an Abstract Syntax Tree (AST)
        const ast = parse(tokens);

        // 3. Evaluate the AST to get the result
        const result = evaluate(ast);

        return result; // Return the final numeric result

    } catch (error) {
        // Catch any errors during tokenization, parsing, or evaluation
        console.error("Processing Error:", error.message);
        // Return NaN to indicate failure
        return NaN;
    }
};

// Note: To use this code, you would import `jsParse` and `jsTransform`
// into your React component and use them in the `useEffect` hook
// that processes the input text.
